// Avisos solo en dev
const warn = (...args) => {
  if (process.env.NODE_ENV !== "development") return;
  // eslint-disable-next-line no-console
  console.warn(...args);
};

// Mapeo por defecto de eventos "humanos" -> eventos GA4
const DEFAULT_EVENT_MAP = {
  // gtm.track('Lead', { value: 123, currency: 'MXN' })
  Lead: (params, cfg) => ({
    event: "generate_lead",
    value: params?.value ?? 1,
    currency: params?.currency ?? cfg.currency ?? "MXN",
    ...params,
  }),

  // Ejemplos útiles comunes
  Purchase: (params) => ({ event: "purchase", ...params }),
  BeginCheckout: (params) => ({ event: "begin_checkout", ...params }),
  AddToCart: (params) => ({ event: "add_to_cart", ...params }),
  ViewItem: (params) => ({ event: "view_item", ...params }),
  SignUp: (params) => ({ event: "sign_up", ...params }),
  Login: (params) => ({ event: "login", ...params }),
};

class GTM {
  CONTAINER_ID;
  initialized = false;

  // Config por defecto
  _config = {
    currency: "MXN", // Moneda por defecto para conversiones
    eventMap: DEFAULT_EVENT_MAP,
    debug: false,
  };

  configure(config = {}) {
    if (config.containerId) {
      this.CONTAINER_ID = config.containerId;
    } else if (!this.CONTAINER_ID) {
      warn("GTM requires a GTM ID to be loaded.");
    }

    // Mezcla de configuración
    this._config = { ...this._config, ...config };
  }

  initialize(config = {}) {
    if (this.initialized) {
      warn("GTM can only be initialized once.");
      return;
    }

    // Evitar ReferenceError en SSR
    if (typeof window === "undefined" || typeof document === "undefined") {
      warn("GTM can be initialized only on client side.");
      return;
    }

    this.configure(config);
    if (!this.CONTAINER_ID) return;

    this.initialized = true;
    // eslint-disable-next-line no-console
    console.log("GTM initialized:", this.CONTAINER_ID);

    const script = document.createElement("script");
    const noscript = document.createElement("noscript");

    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${this.CONTAINER_ID}');
    `;
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${this.CONTAINER_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;

    document.head.insertBefore(script, document.head.childNodes[0]);
    document.body.insertBefore(noscript, document.body.childNodes[0]);
  }

  // Helper interno: convierte "Lead" -> payload GA4 (generate_lead), etc.
  _convertEvent(nameOrPayload, params = {}) {
    // Si ya viene un objeto con "event", se respeta y se fusiona
    if (typeof nameOrPayload === "object" && nameOrPayload !== null) {
      const { event, ...rest } = nameOrPayload;
      if (!event) {
        warn('Custom payload sin "event". Se ignorará.');
        return null;
      }
      return { event, ...rest };
    }

    const name = String(nameOrPayload);
    const mapper = this._config.eventMap?.[name];

    if (typeof mapper === "function") {
      return mapper(params, this._config);
    }

    // Fallback: usa el nombre tal cual como "event"
    return { event: name, ...params };
  }

  // Emitir evento normalizado: gtm.track('Lead', { value: 500 })
  track(nameOrPayload, params = {}) {
    const payload = this._convertEvent(nameOrPayload, params);
    if (!payload) return;

    if (this._config.debug) {
      // eslint-disable-next-line no-console
      console.log("[GTM.track] pushing", payload);
    }

    this.push(payload);
  }

  // Enviar al dataLayer
  // eslint-disable-next-line class-methods-use-this
  push(...args) {
    if (typeof window === "undefined") {
      warn("GTM push works only on client side.");
      return;
    }
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    window.dataLayer.push(...args);
  }
}

// Singleton
export const gtm = new GTM();
